import { CheckoutCatalogEstablishment, CheckoutInfo, CheckoutWonArt } from '../../../../services/Event/types'

/** What the admin changed on the bill before closing the tab — the terminal app's contract. */
export interface ICheckoutAdjustments {
  frameArtIds: number[]
  removedArtIds: number[]
  /** order line id → units taken off */
  removedUnits: Record<number, number>
  /** product id → units added at checkout */
  addedProducts: Record<number, number>
}

export const NO_ADJUSTMENTS: ICheckoutAdjustments = {
  frameArtIds: [],
  removedArtIds: [],
  removedUnits: {},
  addedProducts: {}
}

export interface ICheckoutCatalogProduct {
  id: number
  name: string
  price: number
  establishmentName: string
}

export interface ICheckoutBill {
  keptArts: CheckoutWonArt[]
  removedArts: CheckoutWonArt[]
  /** Bids of the kept artworks, without the fee. */
  bids: number
  systemFee: number
  frames: number
  establishments: number
  added: number
  removed: number
  total: number
}

const cents = (value: number) => Math.round(value * 100)

/**
 * What the terminal would charge with these adjustments. The API checks the same total and refuses
 * less; summed in cents, as there (650.00 + 64.18 is not exact in float).
 */
export function computeBill(
  info: CheckoutInfo,
  adjustments: ICheckoutAdjustments,
  products: ICheckoutCatalogProduct[]
): ICheckoutBill {
  const keptArts = info.won_arts.filter((art) => !adjustments.removedArtIds.includes(art.id))
  const removedArts = info.won_arts.filter((art) => adjustments.removedArtIds.includes(art.id))

  const bids = keptArts.reduce((sum, art) => sum + cents(art.bid), 0)
  const systemFee = keptArts.reduce((sum, art) => sum + cents(art.system_fee ?? 0), 0)
  const frames = keptArts
    .filter((art) => adjustments.frameArtIds.includes(art.id) && (art.frame_value ?? 0) > 0)
    .reduce((sum, art) => sum + cents(art.frame_value ?? 0), 0)

  let establishments = 0
  let removedItems = 0
  info.establishment_orders.forEach((order) =>
    order.items.forEach((item) => {
      const removed =
        item.id === null ? 0 : Math.max(0, Math.min(adjustments.removedUnits[item.id] ?? 0, item.quantity))
      establishments += cents(item.unit_price) * (item.quantity - removed)
      removedItems += cents(item.unit_price) * removed
    })
  )

  const added = Object.entries(adjustments.addedProducts).reduce((sum, [productId, quantity]) => {
    const product = products.find((p) => p.id === Number(productId))
    return sum + (product ? cents(product.price) * quantity : 0)
  }, 0)

  const removed = removedArts.reduce((sum, art) => sum + cents(art.current_bid), 0) + removedItems

  return {
    keptArts,
    removedArts,
    bids: bids / 100,
    systemFee: systemFee / 100,
    frames: frames / 100,
    establishments: establishments / 100,
    added: added / 100,
    removed: removed / 100,
    total: (bids + systemFee + frames + establishments + added) / 100
  }
}

/** The catalog as one list, each product with its establishment. */
export function flattenCatalog(catalog: CheckoutCatalogEstablishment[]): ICheckoutCatalogProduct[] {
  return catalog.flatMap((establishment) =>
    establishment.products.map((product) => ({ ...product, establishmentName: establishment.name }))
  )
}
